import {Stack, Pivot, PivotItem, Spinner, SpinnerSize} from "@fluentui/react";
import {useTranslation} from "react-i18next";
import styles from "./AnalysisPanel.module.css";

import {SupportingContent} from "../SupportingContent";
import {ChatAppResponse} from "../../api";
import {AnalysisPanelTabs} from "./AnalysisPanelTabs";
import {ThoughtProcess} from "./ThoughtProcess";
import {MarkdownViewer} from "../MarkdownViewer";
import {useMsal} from "@azure/msal-react";
import {getHeaders} from "../../api";
import {useLogin, getToken} from "../../authConfig";
import {useState, useEffect} from "react";
import { ArrowExpand20Filled, ArrowMinimize20Filled } from "@fluentui/react-icons";

interface Props {
    className: string;
    activeTab: AnalysisPanelTabs;
    onActiveTabChanged: (tab: AnalysisPanelTabs) => void;
    activeCitation: string | undefined;
    citationHeight: string;
    answer: ChatAppResponse;
    isExpanded: boolean;
    onToggleExpand: () => void;
    onClose?: () => void; // Optional close handler
}


const pivotItemDisabledStyle = {disabled: true, style: {color: "grey"}};

export const AnalysisPanel = ({
    answer,
    activeTab,
    activeCitation,
    citationHeight,
    className,
    onActiveTabChanged,
    isExpanded,
    onToggleExpand,
    onClose
    }: Props) => {

    const isDisabledThoughtProcessTab: boolean = !answer.context.thoughts;
    const isDisabledSupportingContentTab: boolean = !answer.context.data_points;
    const isDisabledCitationTab: boolean = !activeCitation;
    const [citation, setCitation] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    /**
     * Pull the human-readable filename out of the citation URL's `?filename=`
     * query param (set by CitationPill / Search.getCitationUrl). Falls back to
     * the URL's last path segment if absent. We need this because we serve PDFs
     * to the embedded viewer via a blob: URL, and Chrome's built-in PDF
     * download button uses the blob URL's UUID — completely ignoring our
     * server's Content-Disposition header. The custom download button below
     * uses this name so users get the original filename when saving.
     */
    const getDownloadFilename = (): string => {
        if (!activeCitation) return "document";
        try {
            // activeCitation may be relative (/content/...) - URL needs a base.
            const url = new URL(activeCitation, window.location.origin);
            const fromQuery = url.searchParams.get("filename");
            if (fromQuery) return fromQuery;
            const lastSegment = url.pathname.split("/").filter(Boolean).pop() || "document";
            return decodeURIComponent(lastSegment);
        } catch {
            return activeCitation.split("/").pop() || "document";
        }
    };

    const client = useLogin ? useMsal().instance : undefined;
    const {t} = useTranslation();

    const fetchCitation = async () => {
        if (activeCitation) {
            setIsLoading(true);
            setError(null);

            const startTime = performance.now();
            const fileName = activeCitation.split('/').pop() || 'unknown';
            console.log(`🌐 [Frontend] Starting fetch for: ${fileName}`);
            console.log(`🌐 [Frontend] Full URL: ${activeCitation}`);

            try {
                const originalHash = activeCitation.indexOf("#") !== -1 ? activeCitation.split("#")[1] : "";
                let response;

                if (activeCitation.startsWith("/content")) {
                    // Blob storage files
                    console.log(`📂 [Frontend] Using Blob API for ticket content`);
                    response = await fetch(activeCitation, {
                        method: "GET",
                        headers: await getHeaders()
                    });
                } else {
                    // SharePoint files - use POST with blob
                    console.log(`📡 [Frontend] Calling /sharepoint_content endpoint`);
                    const fetchStartTime = performance.now();
                    
                    response = await fetch("/sharepoint_content", {
                        method: "POST",
                        headers: {
                            ...(await getHeaders()),
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({ url: activeCitation })
                    });
                    
                    const fetchTime = performance.now() - fetchStartTime;
                    console.log(`📡 [Frontend] Fetch completed in ${fetchTime.toFixed(2)}ms`);
                }

                console.log(`📊 [Frontend] Response status: ${response.status} ${response.statusText}`);
                const contentLength = response.headers.get('Content-Length');
                const contentType = response.headers.get('Content-Type');
                console.log(`📊 [Frontend] Content-Type: ${contentType}, Content-Length: ${contentLength || 'unknown'}`);

                if (!response.ok) {
                    throw new Error(`Failed to fetch document: ${response.status} ${response.statusText}`);
                }

                console.log(`📥 [Frontend] Starting blob download...`);
                const blobStartTime = performance.now();
                const citationContent = await response.blob();
                const blobTime = performance.now() - blobStartTime;
                console.log(`📥 [Frontend] Blob created: ${citationContent.size} bytes in ${blobTime.toFixed(2)}ms`);

                let citationObjectUrl = URL.createObjectURL(citationContent);
                if (originalHash) {
                    citationObjectUrl += "#" + originalHash;
                }
                setCitation(citationObjectUrl);

                const totalTime = performance.now() - startTime;
                console.log(`✅ [Frontend] Document loaded successfully in ${totalTime.toFixed(2)}ms`);
                setIsLoading(false);
            } catch (err) {
                const totalTime = performance.now() - startTime;
                console.error(`❌ [Frontend] Error after ${totalTime.toFixed(2)}ms:`, err);
                setError(err instanceof Error ? err.message : "Failed to load document");
                setIsLoading(false);
            }
        }
    };

    useEffect(() => {
        fetchCitation();
    }, [activeCitation]);

    const renderFileViewer = () => {
        if (!activeCitation) {
            return null;
        }

        // Show loading spinner
        if (isLoading) {
            return (
                <div className={styles.loadingContainer}>
                    <Spinner size={SpinnerSize.large} label="Loading document..." />
                </div>
            );
        }

        // Show error message
        if (error) {
            return (
                <div className={styles.errorContainer}>
                    <div className={styles.errorIcon}>⚠️</div>
                    <div className={styles.errorMessage}>{error}</div>
                    <button onClick={fetchCitation} className={styles.retryButton}>
                        Retry
                    </button>
                </div>
            );
        }

        // Don't render until citation is loaded
        if (!citation) {
            return null;
        }

        const fileExtension = activeCitation.split(".").pop()?.toLowerCase();

         switch (fileExtension) {
            case "png":
                return <img src={citation} className={styles.citationImg} alt="Citation Image"/>;
            case "md":
                return <MarkdownViewer src={activeCitation}/>;
            default:
                return (
                    <iframe
                        title="Citation"
                        src={citation}
                        width="100%"
                        style={{ height: citationHeight }}
                    />
                );
        }
    };

    return (
        <div
            className={`
                ${isExpanded
                ? "fixed inset-0 w-screen h-screen z-[9999] bg-white p-4 overflow-auto"
                : `${className} bg-white border-l shadow flex flex-col h-full overflow-hidden`
                }
            `}
        >
            {/* Action buttons — aligned in a row, same size (36×36px each) */}
            <div className="absolute top-4 right-4 z-50 flex items-center gap-2">
                {citation && !isLoading && !error && activeTab === AnalysisPanelTabs.CitationTab && (
                    <a
                        href={citation}
                        download={getDownloadFilename()}
                        title={`Download: ${getDownloadFilename()}`}
                        className="flex items-center justify-center w-9 h-9 bg-white rounded-full shadow hover:bg-blue-50 transition-colors"
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10 3v10m0 0l-3-3m3 3l3-3M4 15h12" stroke="#1d4ed8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </a>
                )}
                <button
                    onClick={onToggleExpand}
                    title={isExpanded ? "Minimize panel" : "Expand panel"}
                    className="flex items-center justify-center w-9 h-9 bg-white rounded-full shadow hover:bg-gray-100 transition-colors"
                >
                    {isExpanded ? <ArrowMinimize20Filled /> : <ArrowExpand20Filled />}
                </button>
                {onClose && (
                    <button
                        onClick={onClose}
                        title="Close panel"
                        className="flex items-center justify-center w-9 h-9 bg-white rounded-full shadow hover:bg-red-50 hover:text-red-600 transition-colors"
                    >
                        ✕
                    </button>
                )}
            </div>
    
            <Pivot
                className={className}
                selectedKey={activeTab}
                onLinkClick={pivotItem => pivotItem && onActiveTabChanged(pivotItem.props.itemKey! as AnalysisPanelTabs)}
            >
                <PivotItem
                    itemKey={AnalysisPanelTabs.SupportingContentTab}
                    headerText={t("headerTexts.supportingContent")}
                    headerButtonProps={isDisabledSupportingContentTab ? pivotItemDisabledStyle : undefined}
                >
                    <SupportingContent supportingContent={answer.context.data_points}/>
                </PivotItem>
                <PivotItem
                    itemKey={AnalysisPanelTabs.ThoughtProcessTab}
                    headerText={t("headerTexts.thoughtProcess")}
                    headerButtonProps={isDisabledThoughtProcessTab ? pivotItemDisabledStyle : undefined}
                >
                    <ThoughtProcess thoughts={answer.context.thoughts || []}/>
                </PivotItem>
                <PivotItem
                    itemKey={AnalysisPanelTabs.CitationTab}
                    headerText={t("headerTexts.citation")}
                    headerButtonProps={isDisabledCitationTab ? pivotItemDisabledStyle : undefined}
                >
                    {renderFileViewer()}
                </PivotItem>
            </Pivot>
        </div>
    );    
};
